import { useId } from "@reach/auto-id";
import React, { createRef, useEffect, useRef, useState } from "react";
import { Portal } from "react-portal";
import styled, { css, SimpleInterpolation } from "styled-components";
import { Button, ButtonProps } from "./Button";
import { ClickAway } from "./ClickAway";
import { FocusContainer } from "./FocusContainer";
import { Link } from "./Link";
import { SpinnerButton, SpinnerButtonProps } from "./SpinnerButton";
import { TruncateText } from "./TruncateText";
import { KEY_ESCAPE } from "./constants/keyboardCodes";
import { useWindowSize } from "./hooks/useWindowSize";
import { useScrollLock } from "./hooks/useScrollLock";
import { mediaQueries, style } from "./style";

const modalSmall = "400px";
const modalMedium = "600px";
const modalLarge = "800px";
const modalXLarge = "1150px";

type ModalSize = "medium" | "large" | "xLarge" | "fullPage";

type ModalProps = {
  open?: boolean;
  size?: ModalSize;
  mobileFullScreen?: boolean;
  $fullHeight?: boolean;
  sidebarContent?: React.ReactNode;
  modalHeight?: number;
  backgroundColor?: string;
};

type ModalHeaderProps = {
  stickyHeader?: boolean;
  rightAligned?: boolean;
  titleUrl?: string;
};

type ModalContentProps = {
  $scrollable?: boolean;
  $fullHeight?: boolean;
  size?: ModalSize;
};

type Props = {
  disableClickAway?: boolean;
  open?: boolean;
  size?: ModalSize;
  destroy?: boolean;
  title?: string;
  children: React.ReactNode;
  primaryButtonProps?: SpinnerButtonProps;
  secondaryButtonProps?: ButtonProps;
  primaryButtonLabel?: string;
  secondaryButtonLabel?: string;
  onClose: () => void;
  /**
   * **Deprecated:** since version 34.1.0; use primaryButtonProps instead
   */
  onPrimaryClick?: () => void;
  /**
   * **Deprecated:** since version 34.1.0; use secondaryButtonProps instead
   */
  onSecondaryClick?: () => void;
  /**
   * **Deprecated:** since version 34.1.0; use primaryButtonProps instead
   */
  primaryButtonDisabled?: boolean;
  className?: string;
  image?: string;
  imageAltText?: string;
  scrollable?: boolean;
  hideButtons?: boolean;
  mobileFullScreen?: boolean;
  stickyHeader?: boolean;
  rightAligned?: boolean;
  titleUrl?: string;
  sidebarContent?: React.ReactNode;
  sidebarTitle?: string;
  undismissible?: boolean;
  triggerElementRef?: React.RefObject<any>;
  backgroundColor?: string;
  hasTopHorizontalRule?: boolean;
  hasBottomHorizontalRule?: boolean;
};

const fullHeight = ($fullHeight = false): SimpleInterpolation => {
  return (
    $fullHeight &&
    css`
      flex: 1;
      display: flex;
      flex-direction: column;
    `
  );
};

const ModalWindow = styled.div<ModalContentProps>`
  ${({ $fullHeight }) => fullHeight($fullHeight)}
`;

const Body = styled.div<ModalContentProps>`
  ${({ $fullHeight }) => fullHeight($fullHeight)}
`;

const StyledFocusContainer = styled(FocusContainer)<{
  $fullHeight?: boolean;
}>`
  ${({ $fullHeight }) => fullHeight($fullHeight)}
`;

const Wrapper = styled.div<ModalProps>`
  display: block;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow-y: auto;
  pointer-events: none;
  margin-left: auto;
  margin-right: auto;
  z-index: ${style.vars.zIndex.override};

  /*
  * Because unmounting the modal from the DOM is not immediate, we want
  * to make sure to disable pointer events for the now hidden component.
  */
  ${({ open }) =>
    open &&
    css`
      pointer-events: inherit;
    `}

  ${({ open, $fullHeight }) => open && fullHeight($fullHeight)}
`;

const Backdrop = styled.div<ModalProps>`
  display: none;
  position: fixed;
  background-color: ${({ backgroundColor }) => (backgroundColor ? `${backgroundColor}` : `${style.colors.backdrop}`)};
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${style.vars.zIndex.top};

  ${({ open }) =>
    open &&
    css`
      display: block;
    `}
`;

const ClickAwayWrapper = styled.div<ModalProps>`
  min-height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  ${mediaQueries.smallMin} {
    align-items: center;
  }

  ${({ $fullHeight }) =>
    $fullHeight &&
    css`
      justify-content: flex-end;
    `}
`;

const ModalWrapper = styled.div<ModalProps>`
  ${(props) =>
    props.sidebarContent &&
    css`
      ${mediaQueries.largeMin} {
        flex: 0 0 50%;
        border-left: 1px solid ${style.colors.grey.light};
      }
    `}

  ${(props) =>
    props.sidebarContent &&
    props.modalHeight &&
    css`
      ${mediaQueries.largeMin} {
        max-height: ${props.modalHeight}px;
        overflow: scroll;
        -webkit-overflow-scrolling: touch;
      }
    `}

    ${({ $fullHeight }) => fullHeight($fullHeight)}
`;

const ModalClickAway = styled(ClickAway)<ModalProps>`
  flex: auto;
  background-color: ${style.colors.white};
  border-radius: ${style.vars.borderRadius.base};
  box-shadow: 0px 12px 15px rgba(3, 27, 78, 0.22), 0px 19px 38px rgba(3, 27, 78, 0.2);
  z-index: ${style.vars.zIndex.raised};

  ${(props) =>
    props.mobileFullScreen
      ? css`
          margin: 0;
          max-width: none;
          min-height: 100vh;

          ${mediaQueries.mediumMin} {
            margin: ${props.$fullHeight ? 0 : style.vars.space["4"]};
            max-width: ${modalSmall};
            min-height: ${!props.$fullHeight && "inherit"};

            ${props.size === "medium" &&
            css`
              max-width: ${modalMedium};
            `}

            ${props.size === "large" &&
            css`
              max-width: ${modalLarge};
            `}

            ${props.size === "xLarge" &&
            css`
              max-width: ${modalXLarge};
            `}
          }
        `
      : css`
          margin: ${style.vars.space["3"]};
          max-width: ${modalSmall};

          ${props.size === "medium" &&
          css`
            max-width: ${modalMedium};
          `}

          ${props.size === "large" &&
          css`
            max-width: ${modalLarge};
          `}

  ${props.size === "xLarge" &&
          css`
            max-width: ${modalXLarge};
          `}

  ${mediaQueries.smallMin} {
            margin: ${props.size === "fullPage" ? "0" : style.vars.space["4"]};
          }

          ${props.size === "fullPage" &&
          css`
            min-height: 100vh;
            max-width: 100%;
          `}
        `}

  ${(props) =>
    props.sidebarContent &&
    css`
      ${mediaQueries.mediumMin} {
        max-width: ${modalSmall};
      }

      ${mediaQueries.largeMin} {
        display: flex;
        max-width: ${modalLarge};
      }
    `}

    ${({ $fullHeight }) => fullHeight($fullHeight)}
`;

const CloseButton = styled(Button)`
  height: ${style.vars.height.small};
  width: ${style.vars.height.small};
  min-width: ${style.vars.height.small};
`;

const Header = styled.div<ModalHeaderProps>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  word-break: break-word;
  position: relative;
  background-color: ${style.colors.white};

  ${(props) =>
    props.titleUrl &&
    css`
      justify-content: space-between;
    `}

  ${(props) =>
    props.stickyHeader &&
    css`
      position: sticky;
      top: 0;

      ${mediaQueries.mediumMin} {
        position: ${!props.rightAligned && "relative"};
      }
    `}
`;

const HeaderLinkAndCloseWrapper = styled.div`
  flex: auto 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-right: ${style.vars.whitespace.small};
  /* 78px is the min header height. This ensures that the close button does not shift down when the modal title wraps to a new line. */
  height: 78px;
`;

const showHeaderLink = (size?: ModalSize): size is "medium" | "large" | "xLarge" =>
  size === "medium" || size === "large" || size === "xLarge";

const HeaderLink = styled(Link)<ModalContentProps>`
  display: none;
  margin-right: ${style.vars.whitespace.small} !important;

  ${({ size }) =>
    showHeaderLink(size) &&
    css`
      ${mediaQueries.smallMin} {
        display: inherit;
      }
    `}
`;

const Content = styled.div<ModalContentProps>`
  padding: ${style.vars.space["3"]};

  ${({ $scrollable }) =>
    $scrollable &&
    css`
      max-height: 250px;
      overflow: scroll;
      -webkit-overflow-scrolling: touch;
    `}

  ${({ $fullHeight }) => fullHeight($fullHeight)}

  ${mediaQueries.smallMin} {
    padding: ${style.vars.space["4"]};
  }
`;

const Title = styled.h2`
  margin: 0;
  padding: ${style.vars.space["3"]};
  font-size: ${style.vars.fontSize.small};
  font-weight: 600;
  flex: 1;

  ${mediaQueries.smallMin} {
    font-size: 24px;
    padding: ${style.vars.space["4"]};
  }
`;

const Image = styled.img`
  position: relative;
  z-index: ${style.vars.zIndex.override};
  object-fit: cover;
  height: 130px;
  width: 100%;

  ${mediaQueries.smallMin} {
    height: 190px;
  }
`;

const ControlButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${style.vars.space["3"]};

  ${mediaQueries.smallMin} {
    padding: ${style.vars.space["4"]};
  }
`;

const ControlButton = styled(Button)`
  margin-left: ${style.vars.space["3"]};
`;

const PrimaryControlButton = styled(SpinnerButton)`
  margin-left: ${style.vars.space["3"]};
`;

const HorizontalRule = styled.hr<ModalProps>`
  flex: 0 0 100%;
  border-color: ${style.colors.grey.light};
  margin: 0;

  ${(props) =>
    props.sidebarContent &&
    css`
      ${mediaQueries.largeMin} {
        display: none;
      }
    `}
`;

export const Modal: React.FC<Props> = ({
  disableClickAway = false,
  open,
  size,
  destroy,
  title,
  className,
  onClose,
  primaryButtonProps,
  secondaryButtonProps,
  onPrimaryClick,
  onSecondaryClick,
  primaryButtonDisabled,
  primaryButtonLabel,
  secondaryButtonLabel,
  children,
  image,
  imageAltText,
  scrollable,
  hideButtons,
  mobileFullScreen,
  stickyHeader,
  rightAligned,
  titleUrl,
  sidebarContent,
  sidebarTitle,
  undismissible,
  triggerElementRef,
  backgroundColor,
  hasBottomHorizontalRule,
  hasTopHorizontalRule,
}) => {
  const modalWindowRef = useRef<HTMLDivElement>();
  const [modalHeight, setModalHeight] = useState(0);
  const dialogRef = createRef<HTMLDivElement>();
  const closeButtonRef = createRef<HTMLButtonElement>();
  const dialogHeaderId = `dialog-header-${useId()}`;

  if (process.env.NODE_ENV !== "production" && (!!onPrimaryClick || !!onSecondaryClick || !!primaryButtonDisabled)) {
    /* eslint-disable-next-line no-console */
    console.warn(
      `WARNING! The following fields have been deprecated in the Modal component: 'onPrimaryClick', 'onSecondaryClick', 'primaryButtonDisabled'. Please use 'primaryButtonProps' and 'secondaryButtonProps' instead.`
    );
  }

  const onModalClose = (): void => {
    if (triggerElementRef && triggerElementRef.current && typeof triggerElementRef.current.focus === "function") {
      triggerElementRef.current.focus();
    }
    onClose();
  };

  const closeOnKeyDown = (e: KeyboardEvent): void => {
    if (KEY_ESCAPE === e.key && open) {
      onModalClose();
    }
  };

  useEffect(() => {
    if (open && dialogRef.current) {
      const firstFocusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>("button, input, select, textarea")).filter(
        (el) => el !== closeButtonRef.current
      )[0];

      if (firstFocusable) {
        firstFocusable.focus();
      } else if (closeButtonRef.current) {
        closeButtonRef.current.focus();
      }
    }
  }, [open, dialogRef.current]);

  useEffect(() => {
    document.addEventListener("keydown", closeOnKeyDown, false);

    return () => {
      document.removeEventListener("keydown", closeOnKeyDown, false);
    };
  });

  useEffect(() => {
    if (modalWindowRef.current) {
      setModalHeight(modalWindowRef.current.getBoundingClientRect().height);
    }
  });

  useScrollLock(!!open);

  const { innerWidth: windowWidth } = useWindowSize();

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const showTopHorizontalRule =
    hasTopHorizontalRule ||
    scrollable ||
    rightAligned ||
    (sidebarContent && windowWidth < style.viewports.medium) ||
    (mobileFullScreen && windowWidth < style.viewports.medium);

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const showBottomHorizontalRule = hasBottomHorizontalRule || (!hideButtons && scrollable);

  if (!open) {
    return null;
  }

  return (
    <Portal>
      <Backdrop open={open} backgroundColor={backgroundColor} />
      <Wrapper className={className} open={open} $fullHeight={rightAligned}>
        <ClickAwayWrapper $fullHeight={rightAligned}>
          <ModalClickAway
            onClick={() => {
              if (!disableClickAway) {
                onModalClose();
              }
            }}
            mobileFullScreen={mobileFullScreen}
            $fullHeight={rightAligned}
            sidebarContent={sidebarContent}
            size={size}
          >
            <StyledFocusContainer focusFirstElementOnMount={false} $fullHeight={rightAligned}>
              <ModalWrapper
                aria-modal={true}
                ref={dialogRef}
                role="dialog"
                aria-labelledby={dialogHeaderId}
                sidebarContent={sidebarContent}
                $fullHeight={rightAligned}
                tabIndex={-1}
              >
                <ModalWindow ref={modalWindowRef as React.RefObject<HTMLDivElement>} $fullHeight={rightAligned}>
                  <Body $fullHeight={rightAligned}>
                    {image && <Image src={image} alt={imageAltText} />}
                    {(title || titleUrl) && (
                      <Header stickyHeader={stickyHeader} rightAligned={rightAligned} titleUrl={titleUrl}>
                        {title && (
                          <Title id={dialogHeaderId} tabIndex={-1}>
                            {title}
                          </Title>
                        )}
                        <HeaderLinkAndCloseWrapper>
                          {titleUrl && showHeaderLink(size) && (
                            <HeaderLink type="external" to={titleUrl} size={size} forceExternal>
                              Open in new window
                            </HeaderLink>
                          )}
                          {!undismissible && (
                            <CloseButton
                              ref={closeButtonRef}
                              onClick={onModalClose}
                              tabIndex={0}
                              aria-label="close"
                              icon="close"
                              compact
                              variation="icon"
                            />
                          )}
                        </HeaderLinkAndCloseWrapper>
                        {showTopHorizontalRule && <HorizontalRule />}
                      </Header>
                    )}
                    <Content $scrollable={scrollable} $fullHeight={rightAligned}>
                      {children}
                    </Content>
                    {showBottomHorizontalRule && <HorizontalRule />}
                    {!hideButtons && (
                      <ControlButtons>
                        {!undismissible && (
                          <ControlButton
                            variation="tertiary"
                            onClick={onSecondaryClick ? onSecondaryClick : onModalClose}
                            compact={windowWidth < style.viewports.small}
                            {...secondaryButtonProps}
                          >
                            {secondaryButtonLabel ? secondaryButtonLabel : "Cancel"}
                          </ControlButton>
                        )}
                        <PrimaryControlButton
                          inline
                          disabled={primaryButtonProps?.spinning || primaryButtonDisabled}
                          onClick={onPrimaryClick}
                          variation={destroy ? "danger" : undefined}
                          compact={windowWidth < style.viewports.small}
                          {...primaryButtonProps}
                        >
                          <TruncateText>{primaryButtonLabel ? primaryButtonLabel : "Accept"}</TruncateText>
                        </PrimaryControlButton>
                      </ControlButtons>
                    )}
                  </Body>
                </ModalWindow>
              </ModalWrapper>
              {sidebarContent && (
                <ModalWrapper onClick={undefined} sidebarContent={sidebarContent} modalHeight={modalHeight}>
                  <ModalWindow>
                    <Body>
                      <HorizontalRule sidebarContent={sidebarContent} />
                      <Header>
                        <Title>{sidebarTitle}</Title>
                      </Header>
                      <Content>{sidebarContent}</Content>
                    </Body>
                  </ModalWindow>
                </ModalWrapper>
              )}
            </StyledFocusContainer>
          </ModalClickAway>
        </ClickAwayWrapper>
      </Wrapper>
    </Portal>
  );
};
